import { useReducer, useContext, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { Icon } from "source/shared/components/Icon"
import { useChurchCenterIframe } from "source/shared/hooks/useChurchCenterIframe"
import { useSession } from "source/shared/hooks/useSession"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import { useQRCode } from "source/shared/QRCode"
import { useSuspenseQuery } from "@tanstack/react-query"
import { fetchFlagsQuery } from "../../queries"
import { AmountInput } from "./components/AmountInput"
import { ContinueButton } from "./components/ContinueButton"
import { FormSubmit } from "./components/FormSubmit"
import { LeftPane } from "./components/LeftPane"
import { PageNavigation } from "./components/PageNavigation"
import { PaymentSecurityDisclaimer } from "./components/PaymentSecurityDisclaimer"
import { RightPane } from "./components/RightPane"
import { ConditionalStripeElementsProvider } from "./components/stripe"
import { FormFooter } from "./components/display/FormFooter"
import { FormWrapper } from "./components/display/FormWrapper"
import { PaneGroup } from "./components/display/PaneGroup"
import { DonationFormContext } from "./hooks/useDonationFormContext"
import { useMediaQuery } from "source/giving/hooks/useMediaQuery"
import { LocalStorageStatePersistence } from "./stores/local_storage"
import { reducer } from "./stores/reducer"
import { initializeState } from "./stores/initializeState"
import { useCalculatedTotalAndDesignations } from "./hooks/useCalculatedTotalAndDesignations"
import { useParsedConfig } from "./hooks/useParsedConfig"
import { useValidateProgress } from "./stores/validations"
import { DonationStatus } from "./components/DonationStatus"
import { BrowserNavigationHandler } from "./components/BrowserNavigationHandler"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import useNotifyNativeLoaded from "source/shared/hooks/useNotifyNativeLoaded"
import "./styles/donation-form.css"
import useProductAnalyticsPublishEvent from "source/shared/hooks/useProductAnalyticsPublishEvent"
import { isChurchCenterApp } from "source/Layout"

export function DonationForm() {
  const config = useParsedConfig()
  const { data: flags } = useSuspenseQuery(fetchFlagsQuery())
  const [state, dispatch] = useReducer(reducer, config, initializeState)
  const { inIframe } = useChurchCenterIframe()
  const desktop = useMediaQuery(`(min-width: ${BREAKPOINTS.MD}px)`) && !inIframe
  const {
    meta: { authenticated },
  } = useSession(false)
  const {
    current_organization: { currency },
  } = useContext(ServerRenderedProps)
  const { memo_line_disclaimer: memoDisclaimer = "" } = config

  const {
    focusedPane,
    focusedDesignationIndex,
    designations,
    frequency,
    editFrequencyExpanded,
    errors,
    feeCovered,
    memoChecked,
    memoText,
    formReady,
    stripePaymentMethod,
    savePaymentMethodChecked,
    pollingDonationStatusId,
  } = state

  const { totalCents, calculatedDesignations } =
    useCalculatedTotalAndDesignations(config, state)

  const memoEligible = useMemo(
    () =>
      flags.memo_line &&
      designations.some((designation) => {
        const fund = config.funds.find((f) => f.id === designation.fundId)
        return fund?.memo_line_eligible === true
      }),
    [flags.memo_line, designations, config.funds],
  )

  useQRCode()
  useNotifyNativeLoaded()

  const validateProgress = useValidateProgress(state, config, dispatch)

  const twoColumn = desktop && authenticated
  const showAmount = desktop || focusedPane === "left"
  const showLeftPane = twoColumn || focusedPane === "left"
  const showRightPane = twoColumn || focusedPane === "right"
  const showContinue = !twoColumn && focusedPane === "left"
  const showSubmit = twoColumn || (!twoColumn && focusedPane === "right")
  const isAppGuestMode = sessionStorage.getItem("app_guest_mode") === "true"
  const showMyGivingLink =
    !desktop && !inIframe && authenticated && showLeftPane && !isAppGuestMode
  const isCCA = isChurchCenterApp()
  const publishEvent = useProductAnalyticsPublishEvent()

  useEffect(() => {
    publishEvent("giving.donation-form.loaded", {
      source: isCCA ? "CCA" : desktop ? "CCW Desktop" : "CCW Mobile",
    })
  }, [])

  return (
    <DonationFormContext.Provider value={{ config, dispatch }}>
      <ConditionalStripeElementsProvider
        condition={authenticated}
        {...{
          amountCents: totalCents,
          currency: currency.toLowerCase(),
          frequency,
          stripePaymentMethod,
          savePaymentMethodChecked,
        }}
      >
        {!desktop && (
          <BrowserNavigationHandler {...{ dispatch, focusedPane }} />
        )}
        {desktop && authenticated && <PageNavigation />}
        <FormWrapper authenticated={authenticated}>
          {showAmount && <AmountInput {...{ designations, errors }} />}
          <PaneGroup>
            {showLeftPane && (
              <LeftPane
                {...{
                  designations,
                  frequency,
                  editFrequencyExpanded,
                  errors,
                  focusedDesignationIndex,
                  memo: {
                    checked: memoChecked,
                    disclaimer: memoDisclaimer,
                    eligible: memoEligible,
                    text: memoText,
                  },
                }}
              />
            )}
            {showRightPane && (
              <RightPane
                {...{
                  designations,
                  desktop,
                  totalCents,
                  feeCovered,
                  frequency,
                  stripePaymentMethod,
                  savePaymentMethodChecked,
                }}
              />
            )}
          </PaneGroup>
          <FormFooter>
            {showContinue && (
              <>
                {!authenticated && (
                  <LocalStorageStatePersistence {...{ state }} />
                )}
                <ContinueButton
                  {...{ authenticated, validateProgress, desktop }}
                />
              </>
            )}
            {showMyGivingLink && (
              <Link to="/giving/profile" className="btn text-btn">
                View my giving
                <span className="p-r ml-1 c-tint3" css={{ top: "2px" }}>
                  <Icon symbol="general#right-arrow" />
                </span>
              </Link>
            )}
            {pollingDonationStatusId && (
              <DonationStatus id={pollingDonationStatusId} />
            )}
            {showSubmit && (
              <>
                <FormSubmit
                  {...{
                    state,
                    totalCents,
                    calculatedDesignations,
                    validateProgress,
                    disabled: !formReady,
                    desktop,
                    memoEligible,
                  }}
                />
                <PaymentSecurityDisclaimer />
              </>
            )}
          </FormFooter>
        </FormWrapper>
      </ConditionalStripeElementsProvider>
    </DonationFormContext.Provider>
  )
}
